/**
 * REFACTOR OBJECTIVE:
 * --------------------
 * The purpose of this refactor is to change how the selected option (`selected` state)
 * is set and updated.
 *
 * CURRENT ISSUE:
 * - The current implementation sets `selected` through multiple `useEffect` calls,
 *   often reacting to external props like `myDefaultValue`, `resetValue`, and `options`.
 * - This results in unclear state management and edge cases where the wrong option
 *   might be selected or overwritten.
 *
 * REFACTOR GOAL:
 * - Replace automatic state changes via `useEffect` with controlled updates:
 *   - `selected` will only change when the user manually selects an option.
 *   - A required `defaultValue` prop will be introduced (not optional).
 *   - This default value will initially populate `selected`.
 *   - If the backend sends a saved selection, it should override the initial value.
 *   - If there’s no backend value, fallback to the placeholder option like:
 *     [{ label: 'Select', name: '', placeholder: true }]
 *
 * BENEFITS:
 * - Clearer state flow.
 * - More predictable UI behavior.
 * - Easier to reason about and test.
 */

import * as React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { InputProps, Option } from 'interfaces';
import clsx from 'clsx';
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/24/solid';
import { CypressI } from 'interfaces/cypress';
import { ErrorMessageInput, Icon, Spinner } from '@/components/common';
import { Asterisk } from 'lucide-react';
import { InputText } from '../input-text';
import { useForm } from 'react-hook-form';
import Icons from '@/const/icons';

export interface InputListProps {
	options: Option[];
	defaultOption: Option; // The default option to be selected initially
	handleChange?: (value: string) => void;
	handleSearch?: (value: string) => void;
	onToggleOpen?: (isOpen: boolean) => void;
	maxHeight?: string;
	loading?: boolean;
	classNameLabel?: string; //the label of the input
	classNameList?: string; //the wrapper of the options
	resetValue?: boolean;
	markAsRequired?: boolean;
	isSearch?: boolean;
	showActionButtons?: boolean;
	dynamicContainerWidth?: number;
	containerRef?: (node: HTMLDivElement | null) => void;
	// setInputCreate?: (
	// 	value: string,
	// 	func: any,
	// 	id?: string | number | undefined
	// ) => void;
	setInputDelete?: (id: string) => void;
}

export const InputListV3: React.FC<
	InputProps &
		InputListProps &
		CypressI &
		React.InputHTMLAttributes<HTMLInputElement>
> = ({
	options,
	className,
	classNameList,
	handleChange,
	onToggleOpen,
	maxHeight,
	setValueInput,
	classNameInput,
	classNameTitle,
	loading,
	resetValue,
	markAsRequired,
	isSearch,
	setInputDelete,
	handleSearch,
	containerRef,
	dynamicContainerWidth,
	showActionButtons = false,
	defaultOption,
	classNameLabel,
	...props
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [selected, setSelected] = React.useState<Option>(defaultOption);
	const [filter, setFilter] = React.useState('');
	const [newOptionLabel, setNewOptionLabel] = React.useState<
		string | undefined
	>(undefined);
	const { register, setValue } = useForm();

	React.useEffect(() => {
		if (resetValue) {
			setSelected(defaultOption);
		}
	}, [resetValue, options]); // this is made so it reset if the options change
	React.useEffect(() => {
		if (!isOpen) {
			setValue('input', '');
			setNewOptionLabel(undefined);
		}
	}, [isOpen]);

	const filteredOptions = React.useMemo(
		() =>
			options.filter((option) =>
				(option.label as string).toLowerCase().includes(filter.toLowerCase())
			),
		[options, filter]
	);

	// const inputValue = watch('input');

	// Handles the selection of an option from the dropdown
	const handleSelect = (option: Option) => {
		// Trigger the external change handler if provided
		handleChange?.(option.name);

		// Update the form value (setValueInput is typically from useForm)
		setValueInput?.(props.name, option.name);

		// Update the internal selected state
		setSelected(option);
	};
	return (
		<div className={clsx(className)}>
			<input
				type="text"
				// name={props.name}
				value={selected?.name}
				className="hidden"
				readOnly
				{...props.register(props.name, props.rules)}
			/>
			<Listbox value={selected} onChange={handleSelect}>
				{({ open }) => {
					if (open !== isOpen) {
						setTimeout(() => {
							setIsOpen(open);
							onToggleOpen?.(open);
						}, 100); // this timeout is to  fix  "Warning: Cannot update a component (InputList) while rendering a different component"
					}
					return (
						<>
							<Listbox.Label className="block text-left">
								<label
									htmlFor="name"
									className={clsx(
										'text-sm 2xl:text-base text-black relative',
										classNameTitle
									)}
								>
									{props.title}
									{markAsRequired && (
										<Asterisk className="w-3 h-3 absolute -right-3 top-0 text-red-500" />
									)}
								</label>
							</Listbox.Label>
							<div
								className={clsx('relative', {
									'!cursor-not-allowed group': props.disabled,
								})}
							>
								<Listbox.Button
									className={clsx(
										'mt-[5px] relative w-full text-left border border-opacity-primary flex focus:outline-none bg-white rounded-full px-6 py-3',
										!selected.placeholder && 'capitalize',
										{ 'cursor-pointer': !props.disabled },
										{
											'!border-main-red': props.error,
											'pointer-events-none group-hover:!bg-[#F4F4F4A3]':
												props.disabled,
										},
										classNameInput
									)}
								>
									<span className="flex items-center max-w-[90%]">
										<span
											className={clsx(
												'block truncate text-sm 2xl:text-base text-black',
												{
													'text-opacity-primary':
														selected.placeholder || props.disabled,
												},
												classNameLabel
											)}
										>
											{selected.label}
										</span>
									</span>
									<span
										className={clsx(
											'absolute inset-y-0 right-5 flex items-center ml-3 pointer-events-none'
										)}
									>
										{loading && <Spinner className="mr-1" />}
										{open ? (
											<ChevronUpIcon
												width={20}
												height={20}
												className="text-black"
											/>
										) : (
											<ChevronDownIcon
												width={20}
												height={20}
												className="text-black"
											/>
										)}
									</span>
								</Listbox.Button>
								<Transition
									show={open}
									as={React.Fragment}
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
									afterLeave={() => {
										handleSearch && handleSearch('');
										setFilter('');
									}}
								>
									<Listbox.Options
										static
										style={{
											width:
												typeof dynamicContainerWidth === 'number'
													? dynamicContainerWidth
													: undefined,
										}}
										className={clsx(
											{ hidden: props.disabled },
											maxHeight,
											'py-4',
											'absolute mt-1 z-30 w-full rounded-lg bg-white border border-opacity-primary  focus:outline-none shadow-lg overflow-hidden scroll-custom'
										)}
									>
										{isSearch && (
											<InputText
												name="search"
												register={register}
												placeholder="Search"
												iconLeft={Icons.search}
												className="w-full 2xl:w-auto px-4"
												classNameIcon={clsx(
													'!top-[25%] !w-4 !h-4',
													props.classNameIcon
												)}
												classNameInput={clsx(
													'pl-9 border-none !bg-gray-15 h-8 !mt-0',
													classNameInput
												)}
												onChange={({ target }) => {
													if (handleSearch) {
														handleSearch(target.value);
														return;
													}
													setFilter(target.value);
												}}
												onKeyDown={(e) => {
													e.stopPropagation();
												}}
												onFocus={(e) => {
													e.stopPropagation();
												}}
											/>
										)}
										<div
											className={clsx(
												'overflow-auto scroll-custom max-h-72 !rounded-none',
												{
													'mt-3': isSearch,
												},
												classNameList
											)}
										>
											{loading ? (
												<div className="w-full flex justify-center p-2">
													<Spinner type="loadingPage" />
												</div>
											) : (
												filteredOptions.map((option, index) => {
													const isLastElement = options.length === index + 1;
													return (
														!option.placeholder && (
															<Listbox.Option
																ref={isLastElement ? containerRef : null}
																key={option.name + '-' + index}
																className={({ active }) =>
																	clsx(
																		(selected.name === option.name || active) &&
																			'bg-dark-10',
																		'relative py-3 px-4 cursor-pointer select-none capitalize group'
																	)
																}
																value={option}
															>
																<div className="flex items-center gap-2 ">
																	<Icon
																		src={option.icon || ''}
																		className="!size-4"
																	/>
																	<div
																		className={clsx('relative flex gap-1', {
																			'max-w-[90%]':
																				selected.name === option.name,
																		})}
																	>
																		<span
																			className={clsx(
																				'block truncate text-base text-black',
																				option.classNameItemLabel
																			)}
																		>
																			{option.label}
																		</span>
																		<small
																			className={clsx('text-purple-150 -mt-1', {
																				hidden: option.label !== newOptionLabel,
																			})}
																		>
																			New
																		</small>
																	</div>

																	{selected.name === option.name ? (
																		<span className="absolute right-0 pr-4">
																			<CheckIcon
																				className="w-5 h-5 text-black"
																				aria-hidden="true"
																			/>
																		</span>
																	) : null}
																	<div
																		className={clsx(
																			'flex items-center gap-2 absolute right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200',
																			{
																				hidden:
																					selected.name === option.name ||
																					!showActionButtons ||
																					option.label !== newOptionLabel,
																			}
																		)}
																	>
																		<button
																			type="button"
																			onClick={(e) => {
																				e.stopPropagation();
																				setValue('input', option.label);
																			}}
																		>
																			<Icon
																				src={Icons.editTwo}
																				className="!size-5"
																			/>
																		</button>
																		<button
																			type="button"
																			onClick={(e) => {
																				e.stopPropagation();
																				setValue('input', '');
																				if (setInputDelete) {
																					setInputDelete(option.name);
																				}
																			}}
																		>
																			<Icon
																				src={Icons.trash}
																				className="!size-5 text-main-red"
																			/>
																		</button>
																	</div>
																</div>
															</Listbox.Option>
														)
													);
												})
											)}
										</div>
										{/* {hasInputAdd && (
											<InputText
												name="input"
												title="Others (please specify)"
												register={register}
												placeholder="Write the name here"
												className="w-full 2xl:w-auto mt-4 px-4"
												classNameTitle="!text-[#6E6A73]"
												classNameIcon={clsx('!top-[53%] !size-6')}
												classNameInput={clsx('')}
												setValueInput={setValue}
												iconRight={Icons.sendPurple}
												rightClick={() => {
													setNewOptionLabel(inputValue);
													if (setInputCreate) {
														setInputCreate(inputValue, clearInput, inputId);
													}
												}}
												onKeyDown={(event) => {
													event.stopPropagation();
													if (event.key === 'Enter' && setInputCreate) {
														event.preventDefault();
														const inputValue = (
															event.target as HTMLInputElement
														).value;
														setNewOptionLabel(inputValue);
														setInputCreate(inputValue, clearInput, inputId);
													}
												}}
											/>
										)} */}
									</Listbox.Options>
								</Transition>
							</div>
						</>
					);
				}}
			</Listbox>
			{props.error && props.error.message && (
				<ErrorMessageInput message={props.error.message} />
			)}
		</div>
	);
};
